import React, { memo } from 'react';

import { Count } from '../Count/Count';
import { Divider } from '../Divider/Divider';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { HashLink } from '../Link/subcomponents/HashLink';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';
import { Step as StepType } from './types';
import { getIconProps, getStepStatus } from './utils';

interface Props {
  /** Is it the current step? */
  isCurrent: boolean;
  /** Is it the last step? */
  isLast: boolean;
  /** Are the steps ordered? */
  isOrdered: boolean;
  /** Is step unstarted (i.e. occurs after the last completed and current step) */
  isUnstarted: boolean;
  /** Stepper mode. */
  mode: 'header-nav' | 'sections';
  /** Stepper step. */
  step: StepType;
  /** Step number. Used only if `isOrdered` and in 'header-nav' mode. */
  number?: number;
  /** optional Callback to invoke when a step is clicked. */
  onClick?: (stepId: string) => void;
}

export const Step = memo(
  ({
    isCurrent,
    isLast,
    isOrdered,
    isUnstarted,
    mode,
    number,
    step,
    onClick,
  }: Props) => {
    const { id, isCompleted, description, error, label } = step;
    const isDarkTheme = mode === 'header-nav';
    const isSectionsMode = mode === 'sections';
    const disabled = isOrdered && isUnstarted;
    const textColor = isDarkTheme
      ? ('inverse.text.primary' as const)
      : isUnstarted
        ? ('text.secondary' as const)
        : ('text.primary' as const);
    const textVariant = isDarkTheme
      ? ('section' as const)
      : isCurrent
        ? ('tiny-bold' as const)
        : ('tiny' as const);

    const divider = (
      <Divider
        flex="auto"
        isHorizontal={isDarkTheme}
        theme={isDarkTheme ? 'dark' : 'light'}
        width={isSectionsMode ? undefined : 'm'}
      />
    );

    const pr = isSectionsMode ? undefined : 3;
    const stepStatus = getStepStatus({ isCurrent, step });
    const titleProps = {
      color: textColor,
      hash: id,
      variant: textVariant,
      onClick,
    };
    const TitleElement = onClick ? HashLink : Text;

    return (
      <Layout
        as="li"
        align={isSectionsMode ? 'stretch' : 'center'}
        disabled={disabled}
        spacing={2}
        pr={pr}
      >
        <Layout align="center" direction="column">
          {number && isDarkTheme && !isCompleted && isOrdered ? (
            <Tooltip tooltip={error}>
              <Count
                count={number}
                status={stepStatus}
                theme={isDarkTheme ? 'dark' : 'light'}
              />
            </Tooltip>
          ) : (
            <Tooltip tooltip={error}>
              <Icon
                {...getIconProps({
                  isCurrent,
                  isDarkTheme,
                  isSectionsMode,
                  step,
                })}
              />
            </Tooltip>
          )}
          {isSectionsMode && !isLast && divider}
        </Layout>
        <Layout
          direction={isSectionsMode ? 'column' : 'row'}
          pb={isDarkTheme || isLast ? undefined : 6}
          pr={pr}
        >
          <TitleElement {...titleProps}>{label}</TitleElement>
          {isSectionsMode && description && (
            <Text color="text.secondary" variant="tiny">
              {description}
            </Text>
          )}
        </Layout>
        {isDarkTheme && !isLast && divider}
      </Layout>
    );
  },
);
